import { Check, X } from "lucide-react"
import { Button } from "../../../components/button"
import { PERMIT_TYPES, STATUS_IDS, USER_ROLES_IDS } from "../../../constants/common"
import useAuthStore from "../../../store/Auth"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { useCallback, useMemo, useState } from "react"
import ApprovePermitModal from "../../../components/modal/ApprovePermitModal"
import RejectPermitModal from "../../../components/modal/RejectPermitModal"
import { APP_ROUTES } from "../../../routes"
import { UseFormHandleSubmit } from "react-hook-form"
import toast from "react-hot-toast"
import { useAddUpdateLostStickerRequest } from "../../../hooks/permit/mutation"

interface FormData {
    stickerNumber: string;
    copyNumber: string;
}

interface ActionButtonGroupProps {
    lostVehicleStickerIDCard?: {
        n_LostStickerRequestID: number;
        n_ApprovalStatus: number;
        n_PermitNextApproverRoleID: number;
        permitEndDate: string;
    }

    handleSubmit: UseFormHandleSubmit<FormData>

    isFieldsDisabled: boolean
    isLossDeclaration: boolean
    isPermitApprovedByNextApprover: boolean
}

const ActionButtonGroup = ({
    lostVehicleStickerIDCard, handleSubmit, isFieldsDisabled, isLossDeclaration, isPermitApprovedByNextApprover
}: ActionButtonGroupProps) => {
    const { t } = useTranslation()

    const location = useLocation();

    const { user } = useAuthStore()

    const navigate = useNavigate()

    const searchParams = new URLSearchParams(location.search);

    const key = searchParams.get("ID");

    const { mutateAsync: AddUpdateLostStickerRequest, isPending: isAddUpdateLostStickerRequestLoading } = useAddUpdateLostStickerRequest()

    const onSubmit = useCallback(
        async (data: FormData) => {
            try {
                const body = {
                    n_LostStickerRequestID: key ? Number(key) : 0,
                    s_StickerNumber: data.stickerNumber,
                    s_CopyNumber: data.copyNumber,
                };

                await AddUpdateLostStickerRequest(body);

                toast.success(
                    t(!key ? "permitRequestedSuccessfully" : "permitUpdatedSuccessfully")
                );

                setTimeout(() => {
                    navigate(APP_ROUTES.SEARCH_PERMIT_REQUEST);
                }, 700);
            } catch (error) {
                toast.error(t("somethingWentWrong")!);
            }
        },
        [key, navigate, t]
    );

    const isApproveDisabled = useMemo(
        () =>
            lostVehicleStickerIDCard?.n_ApprovalStatus === STATUS_IDS.EXPIRED ||
            lostVehicleStickerIDCard?.n_ApprovalStatus === STATUS_IDS.APPROVED ||
            lostVehicleStickerIDCard?.n_ApprovalStatus === STATUS_IDS.CANCELED ||
            lostVehicleStickerIDCard?.n_ApprovalStatus === STATUS_IDS.REJECTED ||
            // not fully approved -> only current approver can approve
            (lostVehicleStickerIDCard?.n_ApprovalStatus !== STATUS_IDS.APPROVED &&
                user?.n_RoleID !== lostVehicleStickerIDCard?.n_PermitNextApproverRoleID),
        [
            lostVehicleStickerIDCard?.n_ApprovalStatus,
            lostVehicleStickerIDCard?.n_PermitNextApproverRoleID,
            user?.n_RoleID,
        ]
    );

    const [isApprovePermitModalOpen, setIsApprovePermitModalOpen] =
        useState(false);

    const onApprove = useCallback(
        async () => {
            if (isApproveDisabled) return;

            setIsApprovePermitModalOpen(true);
        },
        [isApproveDisabled, t]
    );

    const isRejectDisabled = useMemo(
        () =>
            lostVehicleStickerIDCard?.n_ApprovalStatus === STATUS_IDS.EXPIRED ||
            lostVehicleStickerIDCard?.n_ApprovalStatus === STATUS_IDS.CANCELED ||
            lostVehicleStickerIDCard?.n_ApprovalStatus === STATUS_IDS.REJECTED,
        [lostVehicleStickerIDCard?.n_ApprovalStatus]
    );

    const [isRejectAppointmentModalOpen, setIsRejectAppointmentModalOpen] =
        useState(false);

    const onReject = useCallback(async () => {
        if (isRejectDisabled) return;

        setIsRejectAppointmentModalOpen(true);
    }, [isRejectDisabled, lostVehicleStickerIDCard?.permitEndDate, t]);

    const isApprovePermitPage = useMemo(
        () => location.pathname === APP_ROUTES.APPROVE_LOST_VEHICLE_STICKER_ID_CARD,
        [location.pathname]
    );

    return (
        <>
            {user?.n_RoleID !== USER_ROLES_IDS.ADMIN && (
                <div className="flex justify-end gap-1">
                    {key && (
                        <>
                            {isApprovePermitPage ? (
                                <>
                                    <Button
                                        onClick={handleSubmit(onApprove)}
                                        Icon={<Check className="w-4" />}
                                        className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                                        disabled={
                                            user?.n_RoleID === USER_ROLES_IDS.STAFF ||
                                            isApproveDisabled
                                        }
                                    >
                                        {t("approve")}
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={onReject}
                                        className="bg-red-600 hover:bg-red-700"
                                        disabled={isRejectDisabled}
                                        Icon={<X className="w-4" />}
                                    >
                                        {t("reject")}
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    onClick={handleSubmit(onSubmit)}
                                    isLoading={
                                        isAddUpdateLostStickerRequestLoading
                                    }
                                    disabled={
                                        !isLossDeclaration ||
                                        isAddUpdateLostStickerRequestLoading ||
                                        isPermitApprovedByNextApprover ||
                                        (user?.n_RoleID === USER_ROLES_IDS.STAFF &&
                                            isFieldsDisabled)
                                    }
                                    className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                                >
                                    {t("update")}
                                </Button>
                            )}
                        </>
                    )}

                    {!key && (
                        <Button
                            onClick={handleSubmit(onSubmit)}
                            isLoading={isAddUpdateLostStickerRequestLoading}
                            disabled={!isLossDeclaration || isAddUpdateLostStickerRequestLoading}
                            className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                        >
                            {t("submit")}
                        </Button>
                    )}
                </div>
            )}

            <ApprovePermitModal
                type={PERMIT_TYPES.LOST_VEHICLE_STICKER_ID_CARD}
                permitId={lostVehicleStickerIDCard?.n_LostStickerRequestID || 0}
                isOpen={isApprovePermitModalOpen}
                onOpenChange={() => setIsApprovePermitModalOpen(false)}
            />

            <RejectPermitModal
                type={PERMIT_TYPES.LOST_VEHICLE_STICKER_ID_CARD}
                permitId={lostVehicleStickerIDCard?.n_LostStickerRequestID || 0}
                isOpen={isRejectAppointmentModalOpen}
                onOpenChange={() => setIsRejectAppointmentModalOpen(false)}
            />
        </>
    )
}

export default ActionButtonGroup
